import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Users, Building2, BarChart3, Plus, Pencil, Trash2, Upload, Download, X, Check } from 'lucide-react';

const API = '/api';
const ROLES = [{value:'staff',label:'일반직원'},{value:'approver',label:'결재자'},{value:'admin',label:'관리자'}];
const TL = {vacation:'휴가 신청',expense:'경비 청구',purchase:'구매 요청',overtime:'초과근무',business_trip:'출장 신청',remote_work:'재택근무',document:'문서 결재',contract:'계약 승인',project:'프로젝트 승인',budget:'예산 요청'};

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('stats');
  const [users, setUsers] = useState([]);
  const [depts, setDepts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const fileRef = useRef();

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [u, d, s] = await Promise.all([
        fetch(API + '/users').then(r => r.json()),
        fetch(API + '/departments').then(r => r.json()),
        fetch(API + '/stats').then(r => r.json()),
      ]);
      setUsers(u); setDepts(d); setStats(s);
    } finally { setLoading(false); }
  };

  const openUserModal = (u = null) => {
    setForm(u ? { ...u } : { name: '', email: '', password: '1234', phone: '', joinDate: '', dept: '', position: '', role: 'staff' });
    setModal(u ? 'editUser' : 'addUser');
  };

  const saveUser = async () => {
    if (!form.name || !form.email) { alert('이름과 이메일은 필수입니다.'); return; }
    setLoading(true);
    try {
      if (modal === 'addUser') {
        const r = await fetch(API + '/users', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
        const newUser = await r.json();
        setUsers(p => [...p, newUser]);
      } else {
        const r = await fetch(API + '/users/' + form.id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
        const updated = await r.json();
        setUsers(p => p.map(x => x.id === updated.id ? updated : x));
      }
      setModal(null);
    } finally { setLoading(false); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await fetch(API + '/users/' + id, { method: 'DELETE' });
    setUsers(p => p.filter(u => u.id !== id));
  };

  const handleExcel = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setLoading(true);
    try {
      const r = await fetch(API + '/users/bulk', { method: 'POST', body: fd });
      const d = await r.json();
      alert(d.count + '명이 등록되었습니다.');
      const u = await fetch(API + '/users').then(r => r.json());
      setUsers(u);
    } finally { setLoading(false); fileRef.current.value = ''; }
  };

  const dlTemplate = () => {
    const a = document.createElement('a');
    a.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent('Name,Email,Password,Phone,Dept,Position,Role\nhong,hong@co.com,1234,010-1234-5678,devteam,staff,staff'));
    a.setAttribute('download', '직원등록양식.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const openDeptModal = (d = null) => {
    setForm(d ? { ...d, positions: d.positions?.join(',') || '' } : { name: '', positions: '' });
    setModal(d ? 'editDept' : 'addDept');
  };

  const saveDept = async () => {
    if (!form.name) { alert('부서명을 입력하세요.'); return; }
    const payload = { ...form, positions: form.positions.split(',').map(p => p.trim()).filter(Boolean) };
    setLoading(true);
    try {
      if (modal === 'addDept') {
        const r = await fetch(API + '/departments', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        const newDept = await r.json();
        setDepts(p => [...p, newDept]);
      } else {
        const r = await fetch(API + '/departments/' + form.id, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        const updated = await r.json();
        setDepts(p => p.map(x => x.id === updated.id ? updated : x));
      }
      setModal(null);
    } finally { setLoading(false); }
  };

  const deleteDept = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await fetch(API + '/departments/' + id, { method: 'DELETE' });
    setDepts(p => p.filter(d => d.id !== id));
  };

  const filtered = users.filter(u => u.name?.includes(search) || u.email?.includes(search) || u.dept?.includes(search) || u.phone?.includes(search));
  const totalPages = pageSize === 0 ? 1 : Math.ceil(filtered.length / pageSize);
  const paginated = pageSize === 0 ? filtered : filtered.slice((page - 1) * pageSize, page * pageSize);
  const rC = { staff: '#e5e7eb', approver: '#dbeafe', admin: '#fde8d8' };
  const rT = { staff: '#374151', approver: '#1e40af', admin: '#92400e' };
  const rL = { staff: '일반직원', approver: '결재자', admin: '관리자' };

  return (
    <div className="page container">
      <div className="page-header">
        <h1 className="page-title">관리자 페이지</h1>
        <p className="page-subtitle">직원 관리, 부서 관리, 결재 현황 통계</p>
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#f3f4f6', padding: 4, borderRadius: 10, width: 'fit-content' }}>
        {[{ id: 'stats', label: '결재 통계' }, { id: 'users', label: '직원 관리 (' + users.length + ')' }, { id: 'depts', label: '부서 관리 (' + depts.length + ')' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: tab === t.id ? '#fff' : 'transparent', color: tab === t.id ? '#4f46e5' : '#6b7280', fontWeight: tab === t.id ? 600 : 400, fontSize: 14, cursor: 'pointer' }}>{t.label}</button>
        ))}
      </div>

      {tab === 'stats' && stats && (
        <div>
          <div className="stats-grid" style={{ marginBottom: 24 }}>
            <div className="stat-card"><div className="stat-icon stat-icon-blue"><Users size={22} color="#4f46e5" /></div><div><div className="stat-num">{stats.totalUsers}</div><div className="stat-label">전체 직원</div></div></div>
            <div className="stat-card"><div className="stat-icon stat-icon-yellow"><BarChart3 size={22} color="#d97706" /></div><div><div className="stat-num">{stats.totalApprovals}</div><div className="stat-label">전체 결재</div></div></div>
            <div className="stat-card"><div className="stat-icon stat-icon-green"><Check size={22} color="#059669" /></div><div><div className="stat-num">{stats.approved}</div><div className="stat-label">승인 완료</div></div></div>
            <div className="stat-card"><div className="stat-icon stat-icon-red"><X size={22} color="#dc2626" /></div><div><div className="stat-num">{stats.rejected}</div><div className="stat-label">반려</div></div></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div className="card">
              <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>월별 결재 현황</h2>
              {Object.entries(stats.monthly).map(([m, d]) => (
                <div key={m} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}><span style={{ fontWeight: 500 }}>{m}</span><span style={{ color: '#6b7280' }}>총 {d.total}건</span></div>
                  <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#f3f4f6' }}>
                    {d.total > 0 && <><div style={{ width: (d.approved / d.total * 100) + '%', background: '#10b981' }} /><div style={{ width: (d.pending / d.total * 100) + '%', background: '#f59e0b' }} /><div style={{ width: (d.rejected / d.total * 100) + '%', background: '#ef4444' }} /></>}
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#9ca3af', marginTop: 3 }}><span>승인 {d.approved}</span><span>대기 {d.pending}</span><span>반려 {d.rejected}</span></div>
                </div>
              ))}
            </div>
            <div className="card">
              <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>유형별 결재 현황</h2>
              {Object.entries(stats.byType).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 13, flex: 1 }}>{TL[type] || type}</span>
                  <div style={{ width: 120, height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}><div style={{ width: (count / stats.totalApprovals * 100) + '%', height: '100%', background: '#4f46e5', borderRadius: 4 }} /></div>
                  <span style={{ fontSize: 13, color: '#6b7280', minWidth: 24, textAlign: 'right' }}>{count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>부서별 결재 현황</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
              {Object.entries(stats.byDept).sort((a, b) => b[1] - a[1]).map(([dept, count]) => (
                <div key={dept} style={{ padding: 14, background: '#f9fafb', borderRadius: 10, textAlign: 'center' }}><div style={{ fontSize: 22, fontWeight: 700, color: '#4f46e5' }}>{count}</div><div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{dept}</div></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'users' && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <input className="form-input" style={{ flex: 1, minWidth: 200 }} placeholder="이름, 이메일, 부서, 전화번호 검색..." value={search} onChange={e => setSearch(e.target.value)} />
            <select className="form-select" style={{ width: 'auto' }} value={pageSize}
              onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
              <option value={20}>20명씩</option>
              <option value={50}>50명씩</option>
              <option value={0}>전체</option>
            </select>
            <button className="btn btn-secondary" onClick={dlTemplate}><Download size={14} /> 양식 다운로드</button>
            <button className="btn btn-secondary" onClick={() => fileRef.current.click()}><Upload size={14} /> 엑셀 업로드</button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleExcel} />
            <button className="btn btn-primary" onClick={() => openUserModal()}><Plus size={14} /> 직원 추가</button>
          </div>
          <div className="card"><div className="table-wrap">
            <table>
              <thead><tr><th>이름</th><th>이메일</th><th>전화번호</th><th>부서</th><th>직위</th><th>입사일</th><th>권한</th><th>관리</th></tr></thead>
              <tbody>
                {filtered.length === 0
                  ? <tr><td colSpan={8} style={{ textAlign: 'center', color: '#9ca3af', padding: 32 }}>직원이 없습니다</td></tr>
                  : paginated.map(u => (
                    <tr key={u.id}>
                      <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div className="avatar" style={{ width: 30, height: 30, fontSize: 12 }}>{u.name?.[0]}</div><span style={{ fontWeight: 500 }}>{u.name}</span></div></td>
                      <td style={{ fontSize: 13, color: '#6b7280' }}>{u.email}</td>
                      <td style={{ fontSize: 13, color: '#6b7280' }}>{u.phone || '-'}</td>
                      <td style={{ fontSize: 13 }}>{u.dept}</td>
                      <td style={{ fontSize: 13 }}>{u.position}</td>
                      <td style={{ fontSize: 13, color: '#6b7280' }}>{u.joinDate || '-'}</td>
                      <td><span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: rC[u.role], color: rT[u.role] }}>{rL[u.role] || u.role}</span></td>
                      <td><div className="table-action">
                        <button className="btn btn-secondary btn-sm" onClick={() => openUserModal(u)}><Pencil size={13} /></button>
                        <button className="btn btn-danger btn-sm" onClick={() => deleteUser(u.id)}><Trash2 size={13} /></button>
                      </div></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div></div>
          {pageSize !== 0 && totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 16 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>이전</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)}
                  style={{ padding: '4px 12px', borderRadius: 8, border: '1px solid ' + (page === p ? '#4f46e5' : '#e5e7eb'),
                    background: page === p ? '#4f46e5' : '#fff', color: page === p ? '#fff' : '#374151', cursor: 'pointer', fontSize: 13 }}>
                  {p}
                </button>
              ))}
              <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>다음</button>
            </div>
          )}
          <div style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: '#9ca3af' }}>
            총 {filtered.length}명 중 {pageSize === 0 ? filtered.length : Math.min(page * pageSize, filtered.length)}명 표시
          </div>
        </div>
      )}

      {tab === 'depts' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={() => openDeptModal()}><Plus size={14} /> 부서 추가</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
            {depts.map(d => (
              <div key={d.id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600 }}>{d.name}</h3>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => openDeptModal(d)}><Pencil size={13} /></button>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteDept(d.id)}><Trash2 size={13} /></button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(d.positions || []).map((p, i) => <span key={i} style={{ padding: '3px 10px', background: '#f0f0ff', color: '#4f46e5', borderRadius: 20, fontSize: 12 }}>{p}</span>)}
                </div>
                <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 10 }}>소속 직원 {users.filter(u => u.dept === d.name).length}명</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 600 }}>{modal === 'addUser' ? '직원 추가' : modal === 'editUser' ? '직원 수정' : modal === 'addDept' ? '부서 추가' : '부서 수정'}</h2>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            {(modal === 'addUser' || modal === 'editUser') && (
              <div>
                <div className="form-group"><label className="form-label">이름 *</label><input className="form-input" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">이메일 *</label><input className="form-input" type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">비밀번호</label><input className="form-input" type="password" value={form.password || ''} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="변경시에만 입력" /></div>
                <div className="form-group"><label className="form-label">입사일</label><input className="form-input" type="date" value={form.joinDate || ''} onChange={e => setForm({ ...form, joinDate: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">전화번호</label><input className="form-input" value={form.phone || ''} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="010-0000-0000" /></div>
                <div className="form-group"><label className="form-label">입사일</label><input className="form-input" type="date" value={form.joinDate || ''} onChange={e => setForm({ ...form, joinDate: e.target.value })} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group"><label className="form-label">부서</label>
                    <select className="form-select" value={form.dept || ''} onChange={e => setForm({ ...form, dept: e.target.value })}>
                      <option value="">선택</option>
                      {depts.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">직위</label>
                    <select className="form-select" value={form.position || ''} onChange={e => setForm({ ...form, position: e.target.value })}>
                      <option value="">선택</option>
                      {(depts.find(d => d.name === form.dept)?.positions || []).map((p, i) => <option key={i} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <div className="form-group"><label className="form-label">권한</label>
                  <select className="form-select" value={form.role || 'staff'} onChange={e => setForm({ ...form, role: e.target.value })}>
                    {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>
            )}
            {(modal === 'addDept' || modal === 'editDept') && (
              <div>
                <div className="form-group"><label className="form-label">부서명 *</label><input className="form-input" value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                <div className="form-group"><label className="form-label">직위 목록 (쉼표로 구분)</label><input className="form-input" value={form.positions || ''} onChange={e => setForm({ ...form, positions: e.target.value })} placeholder="사원, 대리, 과장, 차장, 부장" /></div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>취소</button>
              <button className="btn btn-primary" onClick={modal.includes('User') ? saveUser : saveDept} disabled={loading}>{loading ? '저장 중...' : '저장'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
